/**
 * Renovação da sessão. Sem dependências: quem chama injeta a leitura/gravação dos tokens e a chamada HTTP.
 *
 * O Web mantém uma cópia idêntica deste arquivo em `Web/src/api/renovacao.ts` (ele ainda não consome o `shared/`).
 * Mexeu aqui, mexa lá.
 *
 * Regras:
 * - Só UMA renovação roda por vez; quem chega durante ela espera o mesmo resultado.
 * - Só um "inválido" do servidor derruba a sessão. Falta de rede, timeout e erro 5xx NÃO deslogam.
 */

export interface ParDeTokens {
  accessToken: string
  refreshToken: string
}

/** ok = renovou; recusada = o servidor disse que a sessão não vale mais; sem-rede = não deu para saber. */
export type ResultadoRenovacao = 'ok' | 'recusada' | 'sem-rede'

export interface RenovadorConfig {
  lerAccessToken: () => string | null
  lerRefreshToken: () => string | null
  /** Faz o POST /api/auth/refresh. Deve lançar o erro do axios (com `response`) quando falhar. */
  chamarRefresh: (refreshToken: string) => Promise<ParDeTokens>
  /** Guarda o par novo (o refresh token antigo já não vale). */
  aoRenovar: (tokens: ParDeTokens) => void
  /** A sessão acabou de vez: a plataforma limpa o login e leva a pessoa para a tela de entrada. */
  aoEncerrar: () => void
  /** Web: garante que só uma aba renova por vez (Web Locks). */
  emExclusao?: <T>(tarefa: () => Promise<T>) => Promise<T>
  /** Web: relê o que outra aba gravou antes de decidir renovar. */
  antesDeRenovar?: () => void | Promise<void>
}

export interface Renovador {
  temSessao: () => boolean
  renovar: () => Promise<ResultadoRenovacao>
  /** Renova só se o token de acesso já venceu ou vence em menos de `margemSegundos`. Devolve null se não precisou. */
  renovarSeVencendo: (margemSegundos?: number) => Promise<ResultadoRenovacao | null>
  encerrar: () => void
}

const STATUS_DE_SESSAO_INVALIDA = new Set([400, 401, 403])

function statusDoErro(erro: unknown): number | undefined {
  return (erro as { response?: { status?: number } } | null)?.response?.status
}

function decodificarBase64Url(texto: string): string {
  const base64 = texto.replace(/-/g, '+').replace(/_/g, '/')
  const completo = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  return (globalThis as unknown as { atob: (s: string) => string }).atob(completo)
}

/** Segundos até o JWT expirar (negativo se já expirou). null se não der para ler. */
export function segundosAteExpirar(jwt: string | null, agoraMs: number = Date.now()): number | null {
  if (!jwt) return null
  try {
    const partes = jwt.split('.')
    if (partes.length !== 3) return null
    const exp = (JSON.parse(decodificarBase64Url(partes[1])) as { exp?: number }).exp
    return typeof exp === 'number' ? exp - agoraMs / 1000 : null
  } catch {
    return null
  }
}

export function criarRenovador(c: RenovadorConfig): Renovador {
  let emAndamento: Promise<ResultadoRenovacao> | null = null

  const executar = async (refreshUsado: string | null): Promise<ResultadoRenovacao> => {
    await c.antesDeRenovar?.()
    const atual = c.lerRefreshToken()
    if (!atual) {
      c.aoEncerrar()
      return 'recusada'
    }
    // Outra aba já renovou enquanto esta esperava a vez: o token guardado mudou, não precisa renovar de novo.
    if (refreshUsado && atual !== refreshUsado) return 'ok'

    try {
      c.aoRenovar(await c.chamarRefresh(atual))
      return 'ok'
    } catch (erro) {
      const status = statusDoErro(erro)
      if (status !== undefined && STATUS_DE_SESSAO_INVALIDA.has(status)) {
        c.aoEncerrar()
        return 'recusada'
      }
      return 'sem-rede'
    }
  }

  const renovar = (): Promise<ResultadoRenovacao> => {
    if (emAndamento) return emAndamento
    const usado = c.lerRefreshToken()
    const rodar = () => executar(usado)
    emAndamento = (c.emExclusao ? c.emExclusao(rodar) : rodar()).finally(() => {
      emAndamento = null
    })
    return emAndamento
  }

  return {
    temSessao: () => !!c.lerRefreshToken(),
    renovar,
    renovarSeVencendo: async (margemSegundos = 300) => {
      if (!c.lerRefreshToken()) return null
      const restante = segundosAteExpirar(c.lerAccessToken())
      if (restante !== null && restante > margemSegundos) return null
      return renovar()
    },
    encerrar: c.aoEncerrar,
  }
}
